"use client";

import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import { useLocale, useTranslations } from "next-intl";

import { mapTagColor } from "@/app/utilities/mapTagColor";
import { OfferReference } from "@/types";

import { OfferCard, type OfferCardProps } from "./OfferCard";

/**
 * Maps a resolved `OfferReference` CMS entry to the `OfferCard` composite.
 */
export function CmsOfferCard(props: OfferReference): JSX.Element {
	const locale = useLocale();
	const t = useTranslations("offerCardGallery");
	const tBadge = useTranslations("promotionalBadge");
	const [tagColor, tagLabel] = props?.offer_type?.split("|") ?? [
		"purple",
		""
	];
	const ctas = props?.call_to_action?.slice(0, 2).map((cta) => ({
		component: NextLink,
		children: cta.link.title,
		href: cta.link.href
	}));
	const modalData = props?.details_and_condition_modal;
	const formattedDeadline =
		props?.deadline &&
		new Date(props?.deadline).toLocaleString(locale, {
			month: "long",
			day: "2-digit"
		});
	const promotionalBadge = props?.coupon.coupon_code
		? {
				promoCode: props?.coupon.coupon_code ?? "",
				text: props?.coupon.description ?? "",
				copyLabel: tBadge("copyLabel"),
				confirmationLabel: tBadge("confirmationLabel")
			}
		: undefined;

	return (
		<OfferCard<NextLinkProps>
			audienceTag={{
				kind: "solid",
				size: "sm",
				color: mapTagColor(
					tagColor
				) as OfferCardProps["audienceTag"]["color"],
				label: tagLabel
			}}
			cardTitle={{
				tag: props?.offer_title.headline_tag,
				children: props?.offer_title.headline
			}}
			cardDescription={props?.description ?? ""}
			promotionalBadge={promotionalBadge}
			badgeText={t("badgeDeadline", { deadline: formattedDeadline })}
			badgeCtaLabel={modalData?.modal_title.headline}
			badgeCtaModal={{
				title: modalData?.modal_title.headline ?? "",
				titleTag: modalData?.modal_title.headline_tag,
				description:
					props?.details_and_condition_modal.description ?? ""
			}}
			cta={ctas[0] as OfferCardProps<NextLinkProps>["cta"]}
		/>
	);
}
